/**
 * Planning Timeline Component
 * Displays the list of segments in a clean, professional timeline format
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import SegmentItem from './SegmentItem';
import { PlanningSegment, VehicleAssignment } from '../../types/matchdayPlanning';

interface PlanningTimelineProps {
  segments: PlanningSegment[];
  planningId: number;
  onSegmentUpdate: (segmentId: number, updates: Partial<PlanningSegment>) => Promise<void>;
  onSegmentDelete: (segmentId: number) => void;
  onSegmentEdit: (segmentId: number) => void;
  isReadOnly?: boolean;
  showDrivingTimes?: boolean;
  vehicleAssignments?: VehicleAssignment[];
  onSaveFavorite?: (segmentId: number, planningId: number) => void;
}

const PlanningTimeline: React.FC<PlanningTimelineProps> = ({
  segments,
  planningId,
  onSegmentUpdate,
  onSegmentDelete,
  onSegmentEdit,
  isReadOnly = false,
  showDrivingTimes = false,
  vehicleAssignments = [],
  onSaveFavorite
}) => {
  // Sort segments by sequence order (chronological sorting is handled by backend)
  const sortedSegments = [...segments].sort((a, b) => a.sequenceOrder - b.sequenceOrder);

  // Check if main anchor exists
  const hasMainAnchor = sortedSegments.some(segment => segment.anchorType === 'main');

  // Helper function to check if date changed between segments
  const hasDateChanged = (currentSegment: PlanningSegment, previousSegment: PlanningSegment | null): boolean => {
    if (!previousSegment) return true; // Always show date for first segment
    if (currentSegment.scheduledDate && previousSegment.scheduledDate) {
      // Compare date strings directly (YYYY-MM-DD format) to avoid timezone issues
      return currentSegment.scheduledDate !== previousSegment.scheduledDate;
    }
    return currentSegment.scheduledDate && !previousSegment.scheduledDate;
  };

  // Helper function to format date for display
  const formatDateDisplay = (dateString: string): string => {
    // Parse YYYY-MM-DD format as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };


  // Calculate duration between two consecutive segments in minutes
  const calcGapMinutes = (a: PlanningSegment, b: PlanningSegment): number | null => {
    const parseTime = (t: string) => {
      const parts = t.split(':').map(Number);
      return (parts[0] || 0) * 60 + (parts[1] || 0);
    };

    const aDate = a.scheduledDate || '1970-01-01';
    const bDate = b.scheduledDate || '1970-01-01';

    const [ay, am, ad] = aDate.split('-').map(Number);
    const [by, bm, bd] = bDate.split('-').map(Number);

    const aMs = new Date(ay, am - 1, ad).getTime() + parseTime(a.scheduledTime) * 60000;
    const bMs = new Date(by, bm - 1, bd).getTime() + parseTime(b.scheduledTime) * 60000;

    const diffMin = Math.round((bMs - aMs) / 60000);
    return diffMin;
  };

  const formatGap = (minutes: number): string => {
    if (minutes < 0) return `−${formatGap(Math.abs(minutes))}`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m}min`;
  };

  const formatHM = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // Get weekly offset from the first vehicle assignment (if any)
  const weeklyOffsetHours = vehicleAssignments.length > 0
    ? vehicleAssignments[0].weeklyDrivingHoursOffset || 0
    : 0;
  const weeklyOffsetMinutes = weeklyOffsetHours * 60;

  // Calculate cumulative driving time and shift time up to each gap
  // Driving time: only counts gaps where the vehicle is actually moving (departure/free segments)
  //   → resets at arrival/stopover/main because the driver stops driving there
  // Shift time: elapsed time from the first segment's scheduled time
  const calcCumulativeTimes = (upToIndex: number): { drivingMin: number; shiftMin: number } => {
    let drivingMin = 0;
    // Shift time = time from first segment to current gap position (= next segment's time)
    const firstSeg = sortedSegments[0];
    const nextSeg = sortedSegments[upToIndex + 1];
    const shiftMin = nextSeg && firstSeg ? (calcGapMinutes(firstSeg, nextSeg) ?? 0) : 0;

    for (let i = 0; i <= upToIndex; i++) {
      const seg = sortedSegments[i];
      const next = sortedSegments[i + 1];
      if (!next) break;
      // Only count as driving time if the segment is a departure or free (= vehicle is moving)
      // Arrival, stopover, main = vehicle has stopped → gap after these is NOT driving time
      const isDriving = seg.anchorType === 'departure' || seg.anchorType === 'free';
      if (isDriving) {
        const gap = calcGapMinutes(seg, next);
        if (gap && gap > 0) drivingMin += gap;
      }
    }

    return { drivingMin, shiftMin: Math.max(0, shiftMin) };
  };

  // Determine color for driving time indicator
  const getDrivingColor = (totalMin: number): string => {
    if (totalMin >= 540) return '#d32f2f'; // ≥9h = red
    if (totalMin >= 270) return '#ff9800'; // ≥4.5h = orange (pause needed)
    return '#4CAF50'; // green
  };

  // Determine color for shift time indicator
  const getShiftColor = (totalMin: number): string => {
    if (totalMin >= 900) return '#d32f2f'; // ≥15h = red
    if (totalMin >= 780) return '#ff9800'; // ≥13h = orange (warning)
    return '#2196F3'; // blue
  };

  if (sortedSegments.length === 0) {
    return (
      <div className="planning-timeline">
        <div className="timeline-empty">
          <div className="empty-state">
            <span className="empty-icon"></span>
            <h3>Keine Segmente vorhanden</h3>
            <p>Fügen Sie das erste Segment zu Ihrer Spieltagsplanung hinzu.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="planning-timeline">
      <div className="timeline-header">

        <div className="timeline-grid-header">
          <div className="header-cell header-anchor">Typ</div>
          <div className="header-cell header-date">Datum</div>
          <div className="header-cell header-time">Zeit</div>
          <div className="header-cell header-location">Ort</div>
          <div className="header-cell header-gap"></div>
          <div className="header-cell header-vehicle">Fahrzeug</div>
          <div className="header-cell header-driver">Fahrer</div>
          <div className="header-cell header-actions">Akt.</div>
        </div>
      </div>

      <div className="timeline-body">
        {sortedSegments.map((segment, index) => {
          // Calculate gap from this segment to the next one
          const gapToNext = index < sortedSegments.length - 1
            ? calcGapMinutes(segment, sortedSegments[index + 1])
            : null;

          return (
            <React.Fragment key={segment.id}>
              <SegmentItem
                segment={segment}
                planningId={planningId}
                isReadOnly={isReadOnly}
                gapMinutes={gapToNext}
                onUpdate={(updates) => {
                  if (updates.anchorType === 'main' && hasMainAnchor && segment.anchorType !== 'main') {
                    alert('Es kann nur einen Hauptanker geben.');
                    return;
                  }
                  onSegmentUpdate(segment.id, updates);
                }}
                onDelete={() => onSegmentDelete(segment.id)}
                onEdit={() => onSegmentEdit(segment.id)}
                onSaveFavorite={onSaveFavorite}
              />
              {index < sortedSegments.length - 1 && showDrivingTimes && (() => {
                const gap = calcGapMinutes(segment, sortedSegments[index + 1]);
                if (gap === null) return null;
                const times = calcCumulativeTimes(index);
                return (
                  <div className="segment-gap" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.15rem 0',
                    fontSize: '0.75rem',
                    color: '#6c757d',
                    borderLeft: '2px dashed #dee2e6',
                    marginLeft: '40px',
                    gap: '0.5rem'
                  }}>
                    <span style={{
                      background: '#fff',
                      padding: '0.1rem 0.5rem',
                      borderRadius: '8px',
                      border: `1px solid ${getDrivingColor(times.drivingMin + weeklyOffsetMinutes)}`,
                      color: getDrivingColor(times.drivingMin + weeklyOffsetMinutes),
                      fontWeight: 600
                    }}
                    title={`Lenkzeit: ${formatHM(times.drivingMin)} (+ ${weeklyOffsetHours}h Vorlauf = ${formatHM(times.drivingMin + weeklyOffsetMinutes)})`}
                    >
                      LZ {formatHM(times.drivingMin + weeklyOffsetMinutes)}/9:00
                    </span>
                    <span style={{
                      background: '#fff',
                      padding: '0.1rem 0.5rem',
                      borderRadius: '8px',
                      border: `1px solid ${getShiftColor(times.shiftMin)}`,
                      color: getShiftColor(times.shiftMin),
                      fontWeight: 600
                    }}
                    title={`Schichtzeit ab erstem Segment`}
                    >
                      SZ {formatHM(times.shiftMin)}/15:00
                    </span>
                  </div>
                );
              })()}
            </React.Fragment>
          );
        })}
      </div>

      <div className="timeline-footer">
        <div className="timeline-summary">
          <span className="summary-text">
            {sortedSegments.length} Segmente |{' '}
            {sortedSegments.filter(s => ['departure', 'arrival', 'stopover', 'main'].includes(s.anchorType)).length} Anker |{' '}
            {new Set(sortedSegments.map(s => s.vehicleType)).size} Fahrzeugtypen
          </span>
          {(() => {
            const totalCo2e = sortedSegments.reduce((sum, s) => sum + (s.co2eKg || 0), 0);
            const perPerson = sortedSegments.reduce(
              (sum, s) => sum + ((s.co2eKg || 0) / (s.passengerCount && s.passengerCount > 0 ? s.passengerCount : 1)), 0);
            const missing = sortedSegments.filter(
              s => (s.estimatedDistanceKm ?? 0) > 0 && s.co2eKg == null && s.anchorType !== 'stopover' && s.anchorType !== 'main').length;
            return (
              <span className="summary-text" style={{ marginLeft: 12 }}>
                | CO₂e gesamt: <strong>{totalCo2e.toLocaleString('de-DE', { maximumFractionDigits: 1 })} kg</strong>
                {' '}(≈ {perPerson.toLocaleString('de-DE', { maximumFractionDigits: 1 })} kg/Person)
                {missing > 0 && (
                  <span style={{ color: '#d97706', marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <AlertTriangle size={12} /> {missing} ohne Faktor
                  </span>
                )}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default PlanningTimeline;
